/**
 * @spec docs/specs/features/staking-position-ui.md#wallet-integration
 * Hook to access the selected wallet from WalletContext
 */
import { useWalletContext } from '@/hooks/useWalletContext';
import type { ConnectedWallet } from '@privy-io/react-auth';

export interface UseSelectedWalletReturn {
  /** The currently selected wallet address (checksummed) */
  address: string | null;
  /** Whether a wallet is selected/connected */
  isConnected: boolean;
  /** Whether Privy is still initializing */
  isLoading: boolean;
  /** The currently selected wallet object */
  wallet: ConnectedWallet | null;
  /** All connected wallets */
  wallets: ConnectedWallet[];
  /** Set the selected wallet by address */
  setSelectedAddress: (address: string) => void;
}

/**
 * Hook to access and manage the selected wallet
 *
 * Use this instead of useWalletAddress when you need:
 * - Access to all connected wallets
 * - Ability to change the selected wallet
 * - The full wallet object (for signing operations)
 */
export function useSelectedWallet(): UseSelectedWalletReturn {
  const { selectedAddress, setSelectedAddress, wallets, selectedWallet, isReady } =
    useWalletContext();

  return {
    address: selectedAddress,
    isConnected: !!selectedAddress,
    isLoading: !isReady,
    wallet: selectedWallet,
    wallets,
    setSelectedAddress,
  };
}
